import { ApolloError } from '@apollo/client';
import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { Box } from '@atlaskit/primitives';
import {
  EntitlementDetailFragment,
  FetchProductsForOrganizationPpcQuery,
  Maybe,
  InvoiceGroup,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  ColGroup,
  CustomDynamicResultTable,
  HeadCell,
  SearchResultColumn,
  TableBody,
  TableHead,
  EntitlementIcon,
  CustomPanel,
} from '@css-xp/base';
import {
  NoData,
  emptyStateProp,
  errorStateProp,
  getSelectedColumns,
  renderPlan,
  renderBillingProfile,
  getOrgDetailsHeaders,
  ORG_DETAILS_COLUMNS,
  Usage,
} from '@css-xp/ppc-common';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { NextBillExpiryDate } from '@css-xp/base/entitlement-utils';
import OfficeBuilding from '@atlaskit/icon/glyph/office-building';
import memoize from 'lodash/memoize';
import capitalize from 'lodash/capitalize';
import { routePaths } from '@css-xp/ppc-constants';
import { emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { getStatusForProductSubscription } from '@css-xp/ppc-subscription-table';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { BilledExternallyTag } from '@css-xp/ppc-billed-externally';
import './organisation-product-table.scss';

type ColumnList = HeadCell & SearchResultColumn;
interface Props {
  txaId: string;
  invoiceGroupDetails: InvoiceGroup[];
  invoiceGroupLoading: boolean;
  dataState: {
    error?: ApolloError | undefined;
    loading: boolean;
    data: FetchProductsForOrganizationPpcQuery | undefined;
  };
  version?: number;
  organizationName?: string;
}
export const COLUMNS = {
  PRODUCT: 'Product',
  STATUS: 'Status',
  APPS: 'Apps',
  OFFERING: 'Plan',
  ORGANIZATION_NAME: 'Organization name',
  SITE: 'Site',
  COMPANY: 'Company',
  SOLD_TO_ADDRESS: 'Sold-to address',
  CUSTOMER_CONTACTS: 'Customer contacts',
  BILLED_USERS: 'Billed users/user tier',
  RENEW_IN: 'Renew in',
  BILLING_CYCLE: 'Billing cycle',
  ENTITLEMENT_NUMBER: 'Entitlement number',
  LAST_INVOICE_AMOUNT: 'Last invoice amount',
  CREATED: 'Created',
  ID: 'Entitlement Id',
  CUSTOMER: 'Customer',
  BILLING_PROFILE: 'Billing profile',
  USAGE: 'Usage',
  NEXT_BILL_DATE: 'Next bill date',
};
const CellLoader = () => (
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="60%" marginBottom="0" />
  </SkeletonLoaderWrapper>
);
const getHeaders = memoize(() => {
  return [
    {
      name: 'Product',
      content: COLUMNS.PRODUCT,
      customClassNames: 'col-20',
    },
    {
      name: 'Status',
      content: COLUMNS.STATUS,
    },
    {
      name: 'Plan',
      content: COLUMNS.OFFERING,
    },
    {
      name: 'Usage',
      content: COLUMNS.USAGE,
    },
    {
      name: 'Billing profile',
      content: COLUMNS.BILLING_PROFILE,
    },
    {
      name: 'Billing cycle',
      content: COLUMNS.BILLING_CYCLE,
    },
    {
      name: 'Next bill date',
      content: COLUMNS.NEXT_BILL_DATE,
    },
  ];
});

export const OrganisationProductTable = ({
  txaId,
  invoiceGroupDetails,
  invoiceGroupLoading,
  dataState,
  version,
  organizationName,
}: Props) => {
  const [columnsList] = useState<Array<ColumnList>>(version === 3 ? getOrgDetailsHeaders() : getHeaders());
  const [tableRows, setTableRows] = useState<Maybe<EntitlementDetailFragment>[]>([]);
  const subscriptionsRoutePPC = txaId && getRouteUrl(routePaths.subscriptions, { txaId: txaId });

  const { error, loading, data } = dataState;

  const productSubscriptions = data?.productsForOrganizationPPC;

  useEffect(() => {
    if (!loading) {
      const nodes = productSubscriptions ?? [];
      setTableRows([...nodes]);
    }
  }, [data, loading, productSubscriptions]);

  const renderSubscription = (productSubscription: any) => {
    return (
      <Link className="product-link" to={`${subscriptionsRoutePPC}${productSubscription?.id}`}>
        <div className="product-detail-container">
          <EntitlementIcon
            logoId={productSubscription?.logoId}
            productId={productSubscription?.productId}
            isCloud={true}
            className="avatar medium"
          />
          <div>
            <div className={version === 3 ? '' : 'product-detail-container-title'}>
              {productSubscription?.productName ?? 'Unnamed product'}
            </div>
            <div className="product-detail-container-subtitle">{productSubscription?.entitlementNumber}</div>
            {productSubscription?.isExternallyBilled && <BilledExternallyTag marginLeft="0" />}
          </div>
        </div>
      </Link>
    );
  };

  const getRowData = (productSubscription: any) => {
    const columns: (HeadCell & SearchResultColumn)[] = getSelectedColumns(columnsList);
    if (version === 3) {
      const rowData = columns.map((column) => {
        switch (column.name) {
          case ORG_DETAILS_COLUMNS.SUBSCRIPTION:
            return <td>{renderSubscription(productSubscription)}</td>;
          case ORG_DETAILS_COLUMNS.STATUS:
            return getStatusForProductSubscription(productSubscription);
          case ORG_DETAILS_COLUMNS.PLAN:
            return <td>{renderPlan(productSubscription)}</td>;
          case ORG_DETAILS_COLUMNS.QUANTITY:
            return (
              <td className="col-usage">
                <Usage
                  activeUsers={productSubscription?.activeUsers}
                  chargeElement={productSubscription?.chargeElement}
                  edition={productSubscription?.edition}
                  productId={productSubscription?.productId}
                  userTier={productSubscription?.userTier}
                />
              </td>
            );
          case ORG_DETAILS_COLUMNS.BILLING_PROFILE:
            return <td>{renderBillingProfile(productSubscription, txaId)}</td>;
          case ORG_DETAILS_COLUMNS.RENEW_DATE:
            return <td>{<NextBillExpiryDate item={productSubscription} />}</td>;
        }
      });
      return rowData;
    }
    const rowData = columns.map((column) => {
      switch (column.name) {
        case COLUMNS.PRODUCT:
          return <td>{renderSubscription(productSubscription)}</td>;
        case COLUMNS.STATUS:
          return getStatusForProductSubscription(productSubscription);
        case COLUMNS.OFFERING:
          return <td>{productSubscription?.edition ?? <NoData />}</td>;
        case COLUMNS.USAGE:
          return <td> {productSubscription?.activeUsers} </td>;
        case COLUMNS.BILLING_PROFILE:
          return (
            <td>
              {invoiceGroupLoading ? (
                <CellLoader />
              ) : (
                <Link
                  to={`${getRouteUrl(routePaths.billingProfileDetails, {
                    txaId: txaId,
                    billingProfileId: `${encodeURIComponent(productSubscription?.invoiceGroupId ?? '')}`,
                  })}`}
                >
                  {
                    invoiceGroupDetails?.find(
                      (invoiceGroup) => invoiceGroup?.id === productSubscription?.invoiceGroupId
                    )?.name
                  }
                </Link>
              )}
            </td>
          );
        case COLUMNS.BILLING_CYCLE:
          return (
            <td>
              {productSubscription?.billingFrequency ? capitalize(productSubscription?.billingFrequency) : <NoData />}
            </td>
          );
        case COLUMNS.NEXT_BILL_DATE:
          return <td>{<NextBillExpiryDate item={productSubscription} />}</td>;
      }
    });
    return rowData;
  };

  const renderTabTable = (subscriptionsData: Maybe<EntitlementDetailFragment>[]) => {
    const children = (
      <>
        <ColGroup columns={getSelectedColumns(columnsList)} />
        <TableHead columns={getSelectedColumns(columnsList)} />
        <TableBody>
          {subscriptionsData?.map((productSubscription: any) => {
            return <tr key={productSubscription.id}>{getRowData(productSubscription)}</tr>;
          })}
        </TableBody>
      </>
    );
    return (
      <CustomDynamicResultTable
        className={classNames('organisation-product-container-table')}
        noResults={tableRows.length === 0}
        error={error}
        isLoading={loading}
        emptyStateProps={version === 3 ? emptyStatePropForWrongSearch : emptyStateProp}
        errorStateProps={errorStateProp}
        isPaginationEnabled={false}
        testId="organisation-product-table"
      >
        {children}
      </CustomDynamicResultTable>
    );
  };

  if (!tableRows.length && version !== 3) return null;

  const getHeader = () => {
    if (version === 3) {
      return (
        <Box paddingInlineStart="space.300">
          <div className="flex align-center gap1">
            <OfficeBuilding label="Cloud site" />
            {`Organization: ${organizationName}`}
          </div>
        </Box>
      );
    }
  };

  const renderProductsHeading = () => {
    if (version === 3) {
      if (tableRows.length === 0) {
        return null;
      }
      return <h3 className="mb-16">Organization </h3>;
    }
    return <h3 className="mb-4">Subscriptions under organization</h3>;
  };

  return (
    <div className="organisation-product-container">
      {renderProductsHeading()}
      <div>
        {version === 3 && tableRows.length !== 0 ? (
          <CustomPanel
            testId={`cloud-site`}
            expandedHeader={getHeader()}
            collapsedHeader={getHeader()}
            isCustomExpanded={true}
            isExpanded={true}
            version={version}
          >
            {renderTabTable(tableRows)}
          </CustomPanel>
        ) : (
          renderTabTable(tableRows)
        )}
      </div>
    </div>
  );
};
